import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { Dayjs } from 'dayjs';
import { CoachCalendarService } from '../../../core/services/coach-calendar.service';
import { IAppointment, IAvailabilitySlot, ICoachCalendarSettings } from '../../../core/models/appointment.model';
import { EAppointmentStatus } from '../../../core/enums/appointment-status.enum';
import { EAppointmentType } from '../../../core/enums/appointment-type.enum';
import { isSameDay, minutesOfDay, minutesToTime, timeToMinutes, tz } from '../../../core/helpers/calendar.helper';
import { AppointmentFormModalComponent } from '../appointment-form-modal/appointment-form-modal.component';
import { CancelAppointmentModalComponent } from '../cancel-appointment-modal/cancel-appointment-modal.component';

interface IGridDay {
  date: Dayjs;
  dayOfWeek: number;
  isToday: boolean;
  /** Minute ranges the coach is available on this weekday. */
  ranges: { start: number, end: number }[];
  cards: IGridCard[];
}

interface IGridCard {
  appointment: IAppointment;
  /** Row index the card starts on and how many rows it covers. */
  rowIndex: number;
  rowSpan: number;
}

const DEFAULT_STEP_MINUTES = 15;
const DEFAULT_REGULAR_MINUTES = 30;

@Component({
  selector: 'coach-agenda-week',
  templateUrl: './agenda-week.component.html',
  styleUrl: './agenda-week.component.scss',
})
export class AgendaWeekComponent implements OnChanges, OnDestroy {
  @Input() weekStart!: Dayjs;
  @Input() appointments: IAppointment[] = [];
  @Input() availability: IAvailabilitySlot[] = [];
  @Input() settings?: ICoachCalendarSettings;
  /** Preselects the student in the create modal when the coach came from a student's header. */
  @Input() preselectedStudentId?: number;
  @Output() changed = new EventEmitter();
  @Output() availabilityRequested = new EventEmitter();

  days: IGridDay[] = [];
  /** Row labels, one per step between the earliest and latest availability of the week. */
  rows: { minutes: number, label: string }[] = [];
  /** On small screens a single day is shown at a time. */
  selectedDayIndex = 0;
  /** Meetings that no longer fall inside the availability grid — listed instead of dropped. */
  outsideAppointments: IAppointment[] = [];
  /** While set, clicking a free cell moves this meeting there instead of creating a new one. */
  movingAppointment?: IAppointment;

  appointmentStatus = EAppointmentStatus;
  appointmentType = EAppointmentType;

  destroyed$ = new Subject();

  constructor(
    private calendarService: CoachCalendarService,
    private modalService: NgbModal,
    private toastrService: ToastrService,
    private translate: TranslateService,
  ) {
  }

  ngOnChanges(): void {
    this.buildGrid();
  }

  stepMinutes(): number {
    return this.settings?.slotStepMinutes ?? DEFAULT_STEP_MINUTES;
  }

  regularMinutes(): number {
    return this.settings?.regularDurationMinutes ?? DEFAULT_REGULAR_MINUTES;
  }

  /** A row is bookable when it sits inside an availability range and holds no meeting. */
  isBookable(day: IGridDay, rowMinutes: number): boolean {
    const end = rowMinutes + this.durationFor(this.movingAppointment);
    const insideRange = day.ranges.some(r => (rowMinutes >= r.start) && (end <= r.end));
    if (!insideRange) {
      return false;
    }

    return !day.cards.some(card => {
      // A meeting being moved must not block the slot it currently occupies.
      if (this.movingAppointment && (card.appointment.id === this.movingAppointment.id)) {
        return false;
      }

      const cardStart = minutesOfDay(card.appointment.startsAt);
      const cardEnd = minutesOfDay(card.appointment.endsAt);
      return (rowMinutes < cardEnd) && (end > cardStart);
    });
  }

  cardAt(day: IGridDay, rowIndex: number): IGridCard | undefined {
    return day.cards.find(c => c.rowIndex === rowIndex);
  }

  /** Rows a multi-row card already covers must not render an empty cell underneath. */
  isCoveredByCard(day: IGridDay, rowIndex: number): boolean {
    return day.cards.some(c => (rowIndex > c.rowIndex) && (rowIndex < (c.rowIndex + c.rowSpan)));
  }

  levelKey(appointment: IAppointment): string {
    return appointment.student?.userInfo?.level?.key ?? 'default';
  }


  startMove(appointment: IAppointment) {
    this.movingAppointment = appointment;
  }

  cancelMove() {
    this.movingAppointment = undefined;
  }

  /** A free cell either books a new meeting or receives the one being moved. */
  onCellSelected(day: IGridDay, rowMinutes: number) {
    if (this.movingAppointment) {
      this.moveTo(day, rowMinutes);
      return;
    }

    this.openCreate(day, rowMinutes);
  }

  moveTo(day: IGridDay, rowMinutes: number) {
    const appointment = this.movingAppointment;
    if (!appointment) {
      return;
    }

    const startsAt = this.cellStart(day, rowMinutes);

    this.calendarService.moveAppointment(appointment.id, startsAt.toISOString())
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.movingAppointment = undefined;
        this.toastrService.success(this.translate.instant('agenda.appointment_moved'));
        this.changed.emit();
      }, error => {
        const key = (error?.code === 409) ? 'agenda.errors.slot_taken' : 'agenda.errors.move_failed';
        this.toastrService.error(this.translate.instant(key));
      });
  }

  private durationFor(appointment?: IAppointment): number {
    if (appointment?.type === EAppointmentType.Intro) {
      return this.settings?.introDurationMinutes ?? this.stepMinutes();
    }

    return this.regularMinutes();
  }

  private cellStart(day: IGridDay, rowMinutes: number): Dayjs {
    return day.date.hour(Math.floor(rowMinutes / 60)).minute(rowMinutes % 60).second(0).millisecond(0);
  }

  openCreate(day: IGridDay, rowMinutes: number) {
    const startsAt = this.cellStart(day, rowMinutes);

    const modalRef = this.modalService.open(AppointmentFormModalComponent, {
      size: 'md',
      centered: true,
      animation: true,
    });
    modalRef.componentInstance.startsAt = startsAt;
    modalRef.componentInstance.studentId = this.preselectedStudentId;

    modalRef.closed.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.toastrService.success(this.translate.instant('agenda.appointment_created'));
      this.changed.emit();
    });
  }

  openCancel(appointment: IAppointment) {
    const modalRef = this.modalService.open(CancelAppointmentModalComponent, {
      size: 'md',
      centered: true,
      animation: true,
    });
    modalRef.componentInstance.appointment = appointment;

    modalRef.closed.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.toastrService.success(this.translate.instant('agenda.appointment_cancelled'));
      this.changed.emit();
    });
  }

  copyMeetLink(appointment: IAppointment) {
    if (!appointment.meetLink) {
      return;
    }

    navigator.clipboard.writeText(appointment.meetLink).then(() => {
      this.toastrService.success(this.translate.instant('agenda.meet_link_copied'));
    });
  }

  private buildGrid() {
    if (!this.weekStart) {
      return;
    }

    const today = tz();
    const active = this.appointments.filter(a => a.status !== EAppointmentStatus.Cancelled);

    this.days = Array.from({length: 7}, (_, index) => {
      const date = this.weekStart.add(index, 'day');
      const weekday = date.day();

      return {
        date,
        dayOfWeek: weekday,
        isToday: isSameDay(date, today),
        ranges: this.availability
          .filter(slot => slot.dayOfWeek === weekday)
          .map(slot => ({start: timeToMinutes(slot.startTime), end: timeToMinutes(slot.endTime)}))
          .sort((a, b) => a.start - b.start),
        cards: [],
      };
    });

    this.rows = this.buildRows();

    this.outsideAppointments = [];

    for (const day of this.days) {
      const cards: IGridCard[] = [];

      for (const appointment of active.filter(a => isSameDay(tz(a.startsAt), day.date))) {
        const card = this.toCard(appointment);
        // Availability can be narrowed after a meeting was booked; such a meeting has no row to
        // sit on, so it is surfaced separately rather than silently disappearing.
        if (card.rowIndex < 0) {
          this.outsideAppointments.push(appointment);
          continue;
        }
        cards.push(card);
      }

      day.cards = cards;
    }

    const todayIndex = this.days.findIndex(d => d.isToday);
    this.selectedDayIndex = (todayIndex >= 0) ? todayIndex : 0;
  }

  /**
   * Rows span the union of every availability range in the week. A day without availability simply
   * renders empty cells, which keeps all seven columns aligned on the same time axis.
   */
  private buildRows(): { minutes: number, label: string }[] {
    const ranges = this.days.flatMap(d => d.ranges);
    if (!ranges.length) {
      return [];
    }

    const start = Math.min(...ranges.map(r => r.start));
    const end = Math.max(...ranges.map(r => r.end));

    const rows: { minutes: number, label: string }[] = [];
    for (let minutes = start; minutes < end; minutes += this.stepMinutes()) {
      rows.push({minutes, label: minutesToTime(minutes)});
    }

    return rows;
  }

  private toCard(appointment: IAppointment): IGridCard {
    const startMinutes = minutesOfDay(appointment.startsAt);
    const endMinutes = minutesOfDay(appointment.endsAt);
    // A meeting may start between two labels when the admin changes the step; it is placed on the
    // last row that starts at or before it.
    let rowIndex = this.rows.findIndex(row => row.minutes === startMinutes);
    if (rowIndex < 0) {
      rowIndex = this.rows.reduce((found, row, index) => (row.minutes <= startMinutes ? index : found), -1);
    }
    const span = Math.max(1, Math.round((endMinutes - startMinutes) / this.stepMinutes()));

    return {appointment, rowIndex, rowSpan: span};
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
